import { useCallback, useEffect, useRef, useState } from "react";
import type { DoorOffer } from "./beta-door-preview";
import axios from "axios";
import "./beta-step-detail.css";

// --- API types ---

interface StepInfo {
  id: number;
  name: string;
  type: string;
}

export interface DetailResponse {
  step: StepInfo;
  options: Record<string, string>;
  messages: Array<{ role: "member" | "guide"; text: string }>;
  selection: string | null;
  inferredChoice?: string | null;
  report: { whatIDid?: string; howItFelt?: string } | null;
  notes: string[];
  doorOffer?: DoorOffer | null;
}

const client = axios.create({ withCredentials: true });
function endpoint(memberId: number, journeyId: number) {
  return `/api/secretmissions/editor/beta/${memberId}/journey/${journeyId}/detail/`;
}

// --- Component ---

export function BetaStepDetail({
  memberId,
  journeyId,
  repeatedVisit = false,
  onPreview,
  onPreviewStart,
}: {
  memberId: number;
  journeyId: number;
  repeatedVisit?: boolean;
  onPreview?: (data: DetailResponse) => void;
  onPreviewStart?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(0);
  // ponytail: stable refs for parent callbacks so fetchDetail doesn't churn
  const onPreviewRef = useRef(onPreview);
  const onPreviewStartRef = useRef(onPreviewStart);
  useEffect(() => {
    onPreviewRef.current = onPreview;
  }, [onPreview]);
  useEffect(() => {
    onPreviewStartRef.current = onPreviewStart;
  }, [onPreviewStart]);

  const reset = useCallback(() => {
    reqId.current++;
    setData(null);
    setError("");
    setLoading(false);
  }, []);

  // Guard stale on unmount
  useEffect(
    () => () => {
      reqId.current++;
    },
    [],
  );

  // Reset on member/journey change
  /* eslint-disable react-hooks/set-state-in-effect -- intentional sync reset on dep change */
  useEffect(() => {
    reset();
    setOpen(false);
  }, [memberId, journeyId, reset]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const fetchDetail = useCallback(async () => {
    onPreviewStartRef.current?.();
    const id = ++reqId.current;
    setLoading(true);
    setError("");
    setData(null);

    try {
      const { data: resp } = await client.get<{ result: DetailResponse }>(
        endpoint(memberId, journeyId),
      );
      if (id !== reqId.current) return;
      setData(resp.result);
      onPreviewRef.current?.(resp.result);
    } catch (e) {
      if (id !== reqId.current) return;
      if (axios.isCancel(e)) return;
      setError(
        axios.isAxiosError(e)
          ? e.response?.data?.message || "Failed to load step detail."
          : "Failed to load step detail.",
      );
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [memberId, journeyId]);

  const toggle = () => {
    if (open) {
      reset();
      setOpen(false);
    } else {
      setOpen(true);
      void fetchDetail();
    }
  };

  return (
    <div className="bsd">
      <button
        type="button"
        className="bsd-toggle"
        aria-expanded={open}
        onClick={toggle}
      >
        Read questions & answers
      </button>

      {open && (
        <div className="bsd-panel" role="region" aria-label="Step detail">
          {loading && <p className="bsd-loading">Loading…</p>}

          {error && (
            <div className="bsd-error" role="alert">
              <p>{error}</p>
              <button
                type="button"
                className="bsd-bar"
                onClick={() => void fetchDetail()}
                style={{ display: "inline" }}
              >
                Retry
              </button>
            </div>
          )}

          {data && (
            <StepDetailContent data={data} repeatedVisit={repeatedVisit} />
          )}
        </div>
      )}
    </div>
  );
}

// --- Inner content (split to keep the main component lean) ---

function StepDetailContent({
  data,
  repeatedVisit,
}: {
  data: DetailResponse;
  repeatedVisit: boolean;
}) {
  const { step, options, messages, selection, report, notes } = data;
  const copy = [
    "Title",
    "Subtitle",
    "Message",
    "Submessage",
    "Lines",
    "Lead",
    "BoxBody",
    "WhatLabel",
    "FeltLabel",
  ]
    .map((k) => options[k])
    .filter(Boolean);
  const choices = Object.entries(options)
    .filter(
      ([k, v]) => v && (/^LabelForCLICK/.test(k) || /^Diff[123]Label$/.test(k)),
    )
    .map(([, v]) => v);
  const answer = messages.some((m) => m.role === "member");
  const selectedLabel = selection ? options[`LabelFor${selection}`] : null;
  return (
    <>
      {copy.length > 0 && (
        <section className="bsd-section">
          <div className="bsd-section-title">What they were shown</div>

          {copy.map((text, i) => (
            <p className="bsd-msg-text" key={i}>
              {text}
            </p>
          ))}
          {data.doorOffer && (
            <>
              <div className="bsd-field-label">Doors offered</div>
              <ul>
                {data.doorOffer.doors.map((d) => (
                  <li key={d.family}>
                    {d.family === "love" ? "Left" : "Right"}: {d.label}
                  </li>
                ))}
              </ul>
            </>
          )}
          {choices.length > 0 && (
            <>
              <div className="bsd-field-label">Available choices</div>
              <ul>
                {choices.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
      <section className="bsd-section">
        <div className="bsd-section-title">
          {messages.some((m) => m.role === "guide")
            ? "Recorded conversation"
            : "Their answer"}
        </div>
        {messages.length > 0 ? (
          <ul className="bsd-transcript">
            {messages.map((m, i) => (
              <li className="bsd-msg" key={i}>
                <div className={`bsd-msg-role bsd-msg-role--${m.role}`}>
                  {m.role === "member"
                    ? "Player answered"
                    : "They were asked / told"}
                </div>
                <div className="bsd-msg-text">{m.text}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="bsd-empty">
            {selectedLabel
              ? `Selected: ${selectedLabel}`
              : data.inferredChoice
                ? `Selected: ${data.inferredChoice} (inferred from next step)`
                : repeatedVisit
                  ? "No recorded answer."
                  : "No recorded answer."}
          </p>
        )}
        {!answer && messages.length > 0 && (
          <p className="bsd-empty">
            No player reply was preserved for this visit.
          </p>
        )}
        {!copy.length && !messages.some((m) => m.role === "guide") && (
          <p className="bsd-empty">
            The displayed question was not preserved for this visit.
          </p>
        )}
      </section>
      {report && (report.whatIDid || report.howItFelt) && (
        <section className="bsd-section">
          <div className="bsd-section-title">Saved report summary</div>
          {report.whatIDid && (
            <p>
              <strong>What they did:</strong> {report.whatIDid}
            </p>
          )}
          {report.howItFelt && (
            <p>
              <strong>How it felt:</strong> {report.howItFelt}
            </p>
          )}
        </section>
      )}
      {notes
        .filter((n) => n.includes("resumed") || n.includes("500"))
        .map((n, i) => (
          <p className="bsd-empty" key={i}>
            {n}
          </p>
        ))}
      <details className="bsd-section">
        <summary>Technical details</summary>
        <p>
          Step {step.id}: {step.name} ({step.type})
        </p>
        <p>Recorded event: {selection || "None"}</p>
      </details>
    </>
  );
}
